import { Link, Meta } from '@solidjs/meta';
import { useLocation } from '@solidjs/router';
import { createMemo } from 'solid-js';
import { useStyling } from '~/global-contexts/CompanyStyling';
import { getRequestURL } from '~/utils/host';

const CUSTOMER_SUBDOMAIN_SUFFIXES = ['.with.magicdoor.com', '.with.magicdoor-test.com'];

const isCustomerSubdomainHostname = (hostname?: string) => {
  if (!hostname) return false;
  return CUSTOMER_SUBDOMAIN_SUFFIXES.some((suffix) => hostname.endsWith(suffix));
};

interface CanonicalOptions {
  hostname?: string;
  protocol?: string;
  pathname?: string;
  customDomain?: string;
}

const buildCanonicalUrl = ({ hostname, protocol, pathname, customDomain }: CanonicalOptions) => {
  const resolvedHostname = customDomain || hostname;
  if (!resolvedHostname) return undefined;

  const resolvedProtocol = protocol || 'https:';
  const resolvedPath = pathname?.startsWith('/') ? pathname : `/${pathname || ''}`;

  return `${resolvedProtocol}//${resolvedHostname}${resolvedPath}`;
};

export const CustomMeta = () => {
  const { companyStyling } = useStyling();
  const location = useLocation();
  const requestURL = getRequestURL();

  const currentHostname = () => {
    if (requestURL?.hostname) return requestURL.hostname;
    if (typeof window !== 'undefined') return window.location.hostname;
    return undefined;
  };

  const canonicalUrl = createMemo(() => {
    const protocol = requestURL?.protocol || (typeof window !== 'undefined' ? window.location.protocol : undefined);
    const pathname = location.pathname || requestURL?.pathname || '/';

    return buildCanonicalUrl({
      hostname: currentHostname(),
      protocol,
      pathname,
      customDomain: companyStyling?.()?.customDomain,
    });
  });

  return (
    <>
      <Link rel="icon" href={companyStyling?.()?.iconUrl} />
      <Meta name="theme-color" content={companyStyling?.()?.metadataThemeColor} />
      {isCustomerSubdomainHostname(currentHostname()) && <Meta name="robots" content="noindex, nofollow" />}
      {canonicalUrl() && <Link rel="canonical" href={canonicalUrl()} />}
    </>
  );
};
